import { notifications } from '@mantine/notifications'

import type { LoginResponse } from '@/services/api/api.types'

export function getChannelDeeplinkUrl(authBaseUrl: string | undefined, sponsorId: string | undefined): string {
  if (!sponsorId) return ''
  const authEnv = authBaseUrl ? new URL(authBaseUrl).hostname.split('.')[0] : null
  const appHost = authEnv && authEnv !== 'prod' ? `${authEnv}.app.sidekickplatform.com` : 'app.sidekickplatform.com'
  return `https://${appHost}/?channel=${sponsorId}`
}

/**
 * Gets the "default sponsor" from the available sponsors list.
 *
 * The backend API uses the concept of a "default sponsor" - always the first sponsor
 * in the availableSponsors array. Certain functionality is only guaranteed to work
 * on the default sponsor's environment, including:
 * - Fetching the full list of sponsors via /sponsor-list
 *
 * @param availableSponsors - Array of sponsors returned from login
 * @returns The default sponsor (first in list) or undefined if list is empty
 */
export function getDefaultSponsor(availableSponsors: LoginResponse['availableSponsors']) {
  return availableSponsors?.[0]
}

/**
 * Checks if the app is running in production environment
 * @returns true if running on production URLs
 */
function isProductionEnvironment(): boolean {
  const hostname = window.location.hostname
  return hostname === 'portal.sidekickplatform.com' || hostname === 'prod.portal.sidekickplatform.com'
}

/**
 * Shows a standardized error notification for channel switching failures.
 * In non-production environments, includes debug information like status codes and server responses.
 *
 * @param channelName - The name of the channel being switched to
 * @param errorSource - Either the response object from selectSponsor or a caught error
 */
export function showChannelSwitchError(
  channelName: string,
  errorSource: { statusCode?: number; error?: any; message?: string } | any,
) {
  const isProduction = isProductionEnvironment()

  let errorMessage = `We ran into an issue while switching to your ${channelName} channel. Please try again or contact support@sidekickwellness.com.`

  // Add technical details if not in production
  if (!isProduction) {
    const details: string[] = []

    // Handle response object from selectSponsor
    if ('statusCode' in errorSource || 'error' in errorSource) {
      if (errorSource.statusCode) {
        details.push(`Status: ${errorSource.statusCode}`)
      }
      if (errorSource.error) {
        const errorStr = typeof errorSource.error === 'string' ? errorSource.error : JSON.stringify(errorSource.error)
        details.push(`Response: ${errorStr}`)
      }
    }
    // Handle caught error object
    else {
      if (errorSource.response?.status) {
        details.push(`Status: ${errorSource.response.status}`)
      }
      if (errorSource.response?.data) {
        const errorStr =
          typeof errorSource.response.data === 'string'
            ? errorSource.response.data
            : JSON.stringify(errorSource.response.data)
        details.push(`Response: ${errorStr}`)
      } else if (errorSource.message) {
        details.push(`Error: ${errorSource.message}`)
      }
    }

    if (details.length > 0) {
      errorMessage += `\n\nDebug Info:\n${details.join('\n')}`
    }
  }

  notifications.show({
    title: 'Channel Switch Failed',
    message: errorMessage,
    color: 'red',
    autoClose: false,
  })
}
